import { HttpErrorResponse } from '@angular/common/http'

import {
  BaseErrorDetails,
  BaseOrderPayload,
  BasePointsCurrency,
  BasePricingPayload
} from 'app/_shared/interfaces/BasePoints'
import { OrderData } from 'app/_shared/interfaces/OrderData'

type ErrorPayload = {
  code?: unknown
  error_code?: unknown
  message?: unknown
  available_points?: unknown
  required_points?: unknown
  errors?: { code?: unknown }
  data?: ErrorPayload
}

const readNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const readErrorPayload = (error: unknown): ErrorPayload => {
  if (!(error instanceof HttpErrorResponse) || typeof error.error !== 'object' || !error.error) {
    return {}
  }

  const response = error.error as ErrorPayload
  const details =
    typeof response.data === 'object' && response.data !== null ? response.data : undefined

  return {
    ...response,
    ...details,
    message: response.message ?? details?.message
  }
}

export function isBaseAiEnabled(value: unknown): boolean {
  return value === true || value === 1 || value === '1'
}

export function classifyBaseError(error: unknown, orderSubmission = false): BaseErrorDetails {
  const httpError = error instanceof HttpErrorResponse ? error : null
  const payload = readErrorPayload(error)
  const rawCode = payload.code ?? payload.error_code ?? payload.errors?.code
  const code = typeof rawCode === 'string' ? rawCode.toLowerCase() : ''
  const status = httpError?.status ?? 0

  if (code === 'base_ai_insufficient_points') {
    return {
      kind: 'insufficient_points',
      message: 'You do not have enough Base points to complete this purchase.',
      availablePoints: readNumber(payload.available_points),
      requiredPoints: readNumber(payload.required_points)
    }
  }

  if (code.includes('identity') && code.includes('mismatch')) {
    return {
      kind: 'identity_mismatch',
      message: 'Your SSM identity does not match the verified Base account.'
    }
  }

  if (code.includes('unverified') || code.includes('email_not_verified')) {
    return {
      kind: 'email_unverified',
      message: 'Verify your SSM email address before using Base points.'
    }
  }

  if (
    code.includes('account_not_found') ||
    code.includes('base_account_not_found') ||
    status === 404
  ) {
    return {
      kind: 'account_not_found',
      message:
        'No Base account was found for your verified SSM email. This purchase cannot be completed.'
    }
  }

  if (orderSubmission && (status === 0 || status === 408 || status >= 500)) {
    return {
      kind: 'purchase_status_unknown',
      message:
        'The purchase status is unclear and points may already have been deducted. Check your orders before trying again.'
    }
  }

  if (
    status === 0 ||
    status === 408 ||
    status === 429 ||
    status >= 500 ||
    code.includes('unavailable')
  ) {
    return {
      kind: 'service_unavailable',
      message: 'Base is temporarily unavailable. Please retry the balance check.'
    }
  }

  const backendMessage = typeof payload.message === 'string' ? payload.message : undefined
  return {
    kind: 'unknown',
    message: backendMessage || 'Base points could not be verified. Please try again.'
  }
}

export function formatBasePoints(
  points: number,
  currency: BasePointsCurrency,
  useLongName = false
): string {
  const value = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2
  }).format(points)
  const label = useLongName ? currency.currency_long_name : currency.currency_short_name
  const space = label ? ' ' : ''

  return currency.currency_prefix ? `${label}${space}${value}` : `${value}${space}${label}`
}

export function remainingBasePoints(availablePoints: number, spentPoints: number): number {
  return Math.max(0, availablePoints - spentPoints)
}

export function buildBasePricingPayload(orderData: OrderData): BasePricingPayload {
  const {
    billing_information: _billingInformation,
    product_kit: _productKit,
    recaptcha: _recaptcha,
    ...checkoutData
  } = orderData
  void _billingInformation
  void _productKit
  void _recaptcha

  return {
    ...checkoutData,
    payment_method: 'base_points'
  }
}

export function buildBaseOrderPayload(orderData: OrderData): BaseOrderPayload {
  const { product_kit: _productKit, ...checkoutData } = orderData
  void _productKit

  return {
    ...checkoutData,
    payment_method: 'base_points'
  }
}
